/** Only the server's never-claimed pending state authorizes an automatic retry. */
export interface FrozenConversationRequest extends Record<string, unknown> {
  projectId: number;
  message: string;
  clientUserMessageId: string;
  clientMessageId: string;
  clientTurnId?: string;
}

export const PENDING_CONVERSATION_TEXT = 'Waiting for the preceding request to finish reading. Your message is saved.';
const activeTurns = new Set<string>();
let scopeVersion = 0;
export const conversationScopeVersion = () => scopeVersion;
export function closeConversationScope() { scopeVersion++; }
const key = (projectId: number, turnId: string) => `${projectId}:${turnId}`;
export function hasActiveConversationTurn(projectId: number, turnId: string): boolean {
  return activeTurns.has(key(projectId, turnId));
}
export function claimConversationTransport(projectId: number, turnId: string): (() => void) | null {
  const id = key(projectId, turnId);
  if (activeTurns.has(id)) return null;
  activeTurns.add(id);
  return () => { activeTurns.delete(id); };
}

export function isFrozenConversationRequest(value: unknown, projectId: number): value is FrozenConversationRequest {
  if (!value || typeof value !== 'object') return false;
  const request = value as Record<string, unknown>;
  return request.projectId === projectId && Number.isSafeInteger(projectId) && projectId > 0 &&
    typeof request.message === 'string' && typeof request.clientUserMessageId === 'string' && !!request.clientUserMessageId &&
    typeof request.clientMessageId === 'string' && !!request.clientMessageId && request.clientMessageId !== request.clientUserMessageId &&
    (request.clientTurnId == null || request.clientTurnId === request.clientUserMessageId) &&
    !['desktopRuntime', 'forcedTool', 'uiAction', 'sourceSuggestion', 'conceptSteering', 'followUpContext', 'targetFormats'].some(field => request[field] != null);
}

function pendingDelay(block: string, request: FrozenConversationRequest): number | null {
  let type = '', data = '';
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) type = line.slice(6).trim();
    if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  if (type !== 'conversation_pending') return null;
  let payload: any;
  try { payload = JSON.parse(data); } catch { return null; }
  if (payload?.status !== 'pending' || payload.clientTurnId !== request.clientUserMessageId ||
      (payload.projectId != null && payload.projectId !== request.projectId) || !payload.turnId) return null;
  return Math.min(10_000, Math.max(1_000, Number(payload.retryAfterMs) || 2_000));
}

export function waitForConversationRetry(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = () => { clearTimeout(timer); reject(new DOMException('Conversation transport closed', 'AbortError')); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, ms);
    signal?.addEventListener('abort', abort, {once: true});
    if (signal?.aborted) abort();
  });
}

/** One consumed stream, with identical bytes/IDs on explicitly pending retries.
 * A network failure or already-running/unknown execution is never retried here.
 */
export async function fetchConversationStream(request: FrozenConversationRequest, options: {
  signal?: AbortSignal; isCurrent: () => boolean; fetcher?: typeof fetch;
  wait?: typeof waitForConversationRetry;
}): Promise<Response> {
  const body = JSON.stringify(request);
  const frozen = JSON.parse(body) as FrozenConversationRequest;
  if (!isFrozenConversationRequest(frozen, frozen.projectId)) throw Error('Invalid saved conversation request.');
  const fetcher = options.fetcher ?? fetch;
  const check = () => {
    options.signal?.throwIfAborted();
    if (!options.isCurrent()) throw new DOMException('Conversation is no longer open', 'AbortError');
  };
  const post = async () => {
    check();
    return fetcher('/api/canvas/conversation', {method: 'POST', credentials: 'include',
      headers: {'Content-Type': 'application/json'}, body, signal: options.signal});
  };
  const first = await post();
  if (!first.ok || !first.body) return first;
  let reader = first.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '', retryAfter: number | null = null, closed = false;
  return new Response(new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        while (!closed) {
          options.signal?.throwIfAborted();
          const result = await reader.read();
          if (!result.done) {
            buffer += decoder.decode(result.value, {stream: true}).replace(/\r\n/g, '\n');
            const blocks = buffer.split('\n\n'); buffer = blocks.pop() ?? '';
            for (const block of blocks) {
              const delay = pendingDelay(block, frozen);
              if (delay != null) retryAfter = delay;
              // A later terminal response wins over a preceding queue notice.
              if (/^event:\s*(agent_done|agent_error)\s*$/m.test(block)) retryAfter = null;
            }
            controller.enqueue(result.value); return;
          }
          reader.releaseLock();
          if (retryAfter == null) { closed = true; controller.close(); return; }
          check();
          await (options.wait ?? waitForConversationRetry)(retryAfter, options.signal);
          if (closed) return;
          check();
          retryAfter = null; buffer = '';
          const response = await post();
          if (!response.ok || !response.body) throw Error(`Saved message could not resume (HTTP ${response.status}).`);
          reader = response.body.getReader();
        }
      } catch (error) { closed = true; await reader.cancel().catch(() => {}); controller.error(error); }
    },
    async cancel(reason) { closed = true; await reader.cancel(reason).catch(() => {}); },
  }), {status: first.status, headers: first.headers});
}

/** Reload recovery never invents a request from the current composer/settings. */
export async function recoverPendingConversationTurns(options: {
  projectId: number; signal?: AbortSignal; isCurrent: () => boolean;
  request?: typeof fetch; resume: (request: FrozenConversationRequest) => void;
}): Promise<void> {
  if (!options.isCurrent()) return;
  const response = await (options.request ?? fetch)(`/api/canvas/projects/${options.projectId}/conversation-pending`,
    {credentials: 'include', cache: 'no-store', signal: options.signal});
  if (!response.ok) throw Error('Saved messages are temporarily unavailable.');
  const payload = await response.json();
  if (!options.isCurrent()) return;
  for (const turn of Array.isArray(payload?.turns) ? payload.turns : []) {
    if (turn?.status !== 'pending' || !isFrozenConversationRequest(turn.request, options.projectId) ||
        hasActiveConversationTurn(options.projectId, turn.request.clientUserMessageId)) continue;
    if (!options.isCurrent()) return;
    options.resume(JSON.parse(JSON.stringify(turn.request)));
  }
}
